<script setup lang="ts">
import { watch } from "vue";
import { AddIcon, DeleteIcon } from "../icons";
import { calculationIssue, type Field } from "@yawhub/core";
const fields = defineModel<Field[]>({ required: true });
defineProps<{ existing?: boolean }>();
watch(
  fields,
  (value) => {
    for (const f of value) {
      if (f.type === "rating") f.range ??= { min: 0, max: 5 };
      if (f.calculation) {
        const first = value.find(
          (source) => source.id === f.calculation!.fieldIds[0],
        );
        f.unit = first?.unit ?? "";
      }
    }
  },
  { immediate: true, deep: true },
);
function add() {
  fields.value.push({
    id: crypto.randomUUID(),
    key: "field_" + (fields.value.length + 1),
    label: "",
    type: "text",
    nullable: true,
    queryable: fields.value.filter((f) => f.queryable).length < 20,
    hidden: false,
    active: true,
  });
}
function changeType(field: Field) {
  if (["json", "image"].includes(field.type)) field.queryable = false;
  if (field.type === "rating") field.range ??= { min: 0, max: 5 };
  else delete field.range;
  if (field.type !== "rating") delete field.ratingStyle;
  if (!["date", "datetime"].includes(field.type)) {
    delete field.defaultNow;
    delete field.stampNow;
    if (!field.identifier) delete field.inputHidden;
  }
}
function selectType(field: Field, value: string) {
  field.type = (
    value === "identifier"
      ? "text"
      : value === "calculation"
        ? "decimal"
        : value
  ) as Field["type"];
  if (value === "calculation") {
    field.calculation ??= { op: "average", fieldIds: [] };
    field.nullable = true;
    field.scale ??= 2;
    field.queryable =
      fields.value.filter((f) => f.queryable && f.id !== field.id).length < 20;
  } else delete field.calculation;
  if (value === "identifier")
    field.identifier ??= {
      mode: "sequence",
      prefix: "",
      suffix: "",
      digits: 4,
    };
  else delete field.identifier;
  changeType(field);
}
function move(index: number, by: number) {
  const next = index + by;
  if (next < 0 || next >= fields.value.length) return;
  const copy = [...fields.value];
  [copy[index], copy[next]] = [copy[next]!, copy[index]!];
  fields.value = copy;
}
function remove(index: number) {
  const [removed] = fields.value.splice(index, 1);
  for (const field of fields.value)
    if (field.calculation)
      field.calculation.fieldIds = field.calculation.fieldIds.filter(
        (id) => id !== removed!.id,
      );
}
function calculationSources(field: Field) {
  return fields.value.filter(
    (source) =>
      source.id !== field.id &&
      (field.calculation!.fieldIds.includes(source.id) ||
        (source.active &&
          !source.calculation &&
          ["integer", "decimal", "rating"].includes(source.type))),
  );
}
</script>
<template>
  <div class="field-editor">
    <div class="field-editor-head">
      <span>Feltnavn</span><span>Datatype</span><span>Enhed</span
      ><span>Indstillinger</span>
    </div>
    <div v-for="(f, i) in fields" :key="f.id" class="field-editor-row">
      <label
        ><span class="sr-only">Feltnavn {{ i + 1 }}</span
        ><input
          v-model="f.label"
          placeholder="Fx Omsætning"
          required
          maxlength="100" /></label
      ><label
        ><span class="sr-only">Datatype {{ i + 1 }}</span
        ><select
          :value="
            f.calculation ? 'calculation' : f.identifier ? 'identifier' : f.type
          "
          @change="selectType(f, ($event.target as HTMLSelectElement).value)"
        >
          <option value="text">Tekst</option>
          <option value="identifier">ID</option>
          <option value="integer">Heltal</option>
          <option value="decimal">Decimaltal</option>
          <option value="calculation">Beregning</option>
          <option value="rating">Skala</option>
          <option value="boolean">Ja / nej</option>
          <option value="date">Dato</option>
          <option value="datetime">Dato og tid</option>
          <option value="enum">Valgliste</option>
          <option value="json">JSON</option>
          <option value="image">Billede</option>
        </select></label
      ><input
        v-model="f.unit"
        aria-label="Enhed"
        :placeholder="f.calculation ? 'Ingen enhed' : 'Fx DKK'"
        :readonly="!!f.calculation"
      />
      <div class="field-toggles">
        <button
          type="button"
          class="text-button"
          :disabled="i === 0"
          :aria-label="'Flyt ' + (f.label || 'felt ' + (i + 1)) + ' op'"
          @click="move(i, -1)"
        >
          Op
        </button>
        <button
          type="button"
          class="text-button"
          :disabled="i === fields.length - 1"
          :aria-label="'Flyt ' + (f.label || 'felt ' + (i + 1)) + ' ned'"
          @click="move(i, 1)"
        >
          Ned
        </button>
        <label
          ><input
            v-model="f.nullable"
            type="checkbox"
            :disabled="!!f.calculation"
          />Må være tom</label
        ><label
          ><input
            v-model="f.queryable"
            type="checkbox"
            :disabled="['json', 'image'].includes(f.type)"
          />Kan forespørges</label
        ><label v-if="existing"
          ><input v-model="f.hidden" type="checkbox" />Skjul</label
        ><label v-if="existing"
          ><input v-model="f.active" type="checkbox" />Aktiv</label
        ><button
          v-if="!existing"
          class="icon-button danger"
          aria-label="Fjern felt"
          @click.prevent="remove(i)"
        >
          <DeleteIcon :size="17" />
        </button>
      </div>
      <div v-if="f.calculation" class="calculation-settings full-span">
        <div class="calculation-controls">
          <label
            >Beregn<select v-model="f.calculation.op">
              <option value="average">Gennemsnit af felter</option>
            </select></label
          >
          <label
            >Decimaler<input
              v-model.number="f.scale"
              type="number"
              min="0"
              max="18"
              required
              :aria-label="'Decimaler for ' + (f.label || 'felt ' + (i + 1))"
          /></label>
        </div>
        <fieldset class="calculation-fields">
          <legend>Vælg mindst to talfelter eller skalaer</legend>
          <label
            v-for="source in calculationSources(f)"
            :key="source.id"
            class="checkbox"
          >
            <input
              v-model="f.calculation.fieldIds"
              type="checkbox"
              :value="source.id"
            />
            {{ source.label || "Unavngivet felt"
            }}<span v-if="!source.active" class="muted"> (inaktivt)</span
            ><span v-if="source.unit" class="muted"> · {{ source.unit }}</span>
          </label>
          <p v-if="!calculationSources(f).length" class="muted small">
            Tilføj talfelter eller skalaer for at lave et gennemsnit.
          </p>
        </fieldset>
        <p class="muted small">
          Felterne vægter lige meget og skal have samme enhed eller skala. Tomme
          felter springes over; 0 tæller med. Resultatet afrundes til det valgte
          antal decimaler.
        </p>
        <p v-if="calculationIssue(f, fields)" class="error" role="status">
          {{ calculationIssue(f, fields) }}
        </p>
      </div>
      <label v-if="f.type === 'enum'" class="full-span"
        >Valgmuligheder, adskilt med komma<input
          :value="f.options?.join(', ')"
          @input="
            f.options = ($event.target as HTMLInputElement).value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          "
      /></label>
      <div
        v-if="f.type === 'rating' && f.range"
        class="scale-settings full-span"
      >
        <label
          >Fra<input
            v-model.number="f.range.min"
            type="number"
            min="0"
            :max="f.range.max - 1"
            step="1"
            required
            :aria-label="'Skala fra, felt ' + (i + 1)"
        /></label>
        <label
          >Til<input
            v-model.number="f.range.max"
            type="number"
            :min="f.range.min + 1"
            :max="Math.min(100, f.range.min + 20)"
            step="1"
            required
            :aria-label="'Skala til, felt ' + (i + 1)"
        /></label>
        <label
          >Vis skala som<select v-model="f.ratingStyle">
            <option :value="undefined">Tal</option>
            <option value="stars">Stjerner</option>
            <option value="slider">Slider</option>
            <option value="dots">Prikker</option>
          </select></label
        >
      </div>
      <div
        v-if="['date', 'datetime'].includes(f.type)"
        class="field-options full-span"
      >
        <label class="checkbox"
          ><input type="checkbox" v-model="f.defaultNow" />Udfyld med nu ved
          oprettelse</label
        >
        <label class="checkbox"
          ><input type="checkbox" v-model="f.stampNow" />Vis knappen Sæt til
          nu</label
        >
        <label class="checkbox"
          ><input type="checkbox" v-model="f.inputHidden" />Skjul i
          indtastning</label
        >
        <p class="muted small">
          Automatiske værdier sættes kun på nye rækker. Dato bruger dansk
          tidszone.
        </p>
      </div>
      <div v-if="f.identifier" class="field-options full-span">
        <label
          >ID-format<select
            v-model="f.identifier.mode"
            @change="f.identifier.mode === 'manual' && (f.inputHidden = false)"
          >
            <option value="sequence">Løbende nummer</option>
            <option value="uuid">GUID</option>
            <option value="manual">Manuel indtastning</option>
          </select></label
        >
        <template v-if="f.identifier.mode !== 'manual'">
          <label
            >Præfiks<input
              v-model="f.identifier.prefix"
              maxlength="40"
              placeholder="Fx SAG-"
          /></label>
          <label
            >Suffiks<input
              v-model="f.identifier.suffix"
              maxlength="40"
              placeholder="Fx -DK"
          /></label>
          <label v-if="f.identifier.mode === 'sequence'"
            >Mindst antal cifre<input
              v-model.number="f.identifier.digits"
              type="number"
              min="1"
              max="12"
          /></label>
          <label class="checkbox"
            ><input type="checkbox" v-model="f.inputHidden" />Skjul i
            indtastning</label
          >
          <p class="muted small">
            ID tildeles ved oprettelse og bevares ved ændringer. Løbende numre
            starter ved 1 pr. felt og genbruges ikke.
          </p>
        </template>
      </div>
    </div>
    <button
      class="button secondary"
      type="button"
      :disabled="fields.length >= 100"
      @click="add"
    >
      <AddIcon :size="17" />Tilføj felt
    </button>
  </div>
</template>
<style scoped lang="scss" src="../styles/components/FieldEditor.scss"></style>
